import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  ReactChild,
  Suspense,
  useCallback,
} from 'react';
import {
  SpaceCategory,
  Space,
  NOTIFICATION_EVENTS,
} from '@cocoplatform/coco-rtc-shared';
import cc from 'classcat';
import SpaceCard from 'components/space-card/SpaceCard';
import C from './UserSpacesExplorer.css';
import DotSeparator from 'components/item-list/Separator';
import IconButton from '@mui/material/IconButton';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { RequestHandle } from 'utils/hooks/useRequest';
import LoaderMessage from 'components/loader/LoaderMessage';
import {
  SortCriteria,
  SortInput,
  spaceCreatedEvt,
  spaceEndedEvt,
  SpaceSection,
  getSpaceSections,
  useSpacesQuery,
  getWorldCorridorSections,
  getPublicSpaceSections,
} from 'atoms/spaces';
import range from 'lodash/range';
import { useRecoilState } from 'recoil';
import { spaceFiltersAtom } from 'atoms/space-filters';
import TagList from 'components/tag-list/TagList';
import { visitSpaceSectionEvt } from 'utils/events/space';
import { useEvt } from 'evt/hooks';
import { useQueryParams } from 'utils/hooks/useQueryParams';
import FlexRow from 'components/flex/FlexRow';
import FlexSep from 'components/flex/FlexSep';
import SortIcon from '!!react-svg-loader!./icon--sort-spaces.svg';
import { useCommunityDetails } from 'atoms/community-details';
import NewSpaceCard from 'components/space-card/NewSpaceCard';
import { Redirect, useHistory } from 'react-router-dom';
import * as CMR from 'constants/community-roles';
import LoadingButton from '@mui/lab/LoadingButton';
import { useWindowSize } from 'rooks';
import ROUTES from '../../constants/routes';
import Alert from '@mui/material/Alert';
import { NARROW_LAYOUT_THRESHOLD } from 'components/dashboard-layout/breakpoints';
import { notificationsEvt } from 'components/notification-menu/NotificationTrigger';
import { SpaceExplorerStateAtom } from 'components/space-explorer-panel/SpaceExplorerPanel';
import { Trans, msg, t } from '@lingui/macro';
import { Jam, getJamsLocal } from '../../atoms/jams';
import JamCard from '../jam-card/JamCard';
import CocoJamIcon from '!!react-svg-loader!../world-corridor-panel/icons/icon--coco-jam.svg';
import RemixSpaceIcon from '!!react-svg-loader!../world-corridor-panel/icons/icon--remix-space.svg';
import ParticipantsIcon from '!!react-svg-loader!./icon--space-participants.svg';
import { LightTooltip } from '../min-sidebar-community-list/MinSidebarCommunityList';
import {
  Button,
  Checkbox,
  Divider,
  Link,
  ListItem,
  Popover,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useLingui } from '@lingui/react';
import NewSpaceModal from 'components/new-space-modal/NewSpaceModal';
import swal from 'sweetalert2';
import HorizontalCarousal from 'components/community-dashboard-header/HorizontalCarousal';
import { getThemeLocalizedNames, useThemeSelection } from 'atoms/space-themes';
import { Height } from '@mui/icons-material';
import NewSpacesIcon from '!!react-svg-loader!../space-explorer-panel/icon--new-spaces.svg';
import MySpacesIcon from '!!react-svg-loader!../space-explorer-panel/icon--my-spaces.svg';
import OtherSpacesIcon from '!!react-svg-loader!../space-explorer-panel/icon--other-spaces.svg';
import { Box } from '@mui/system';
import { ScrollableContainer } from 'components/scrollable-container/ScrollableContainer';
import { useTutorial } from 'components/onboarding-tutorials/tutorial-atom';
import { GETTING_STARTED_TUTORIAL } from 'components/onboarding-tutorials/getting-started';
import InfoIcon from '@mui/icons-material/Info';
import { useUserProfile } from 'atoms/user-profile';

const sectionIcons = {
  'coco-jams': CocoJamIcon,
  'spaces-to-remix': RemixSpaceIcon,
  'new-spaces-to-join': NewSpacesIcon,
  'my-coco-spaces': MySpacesIcon,
  'explore-other-spaces': OtherSpacesIcon,
};

function SectionInfo(p: { text: React.ReactNode }) {
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='flex-end'
      sx={{
        color: 'var(--color-primary)',
        cursor: 'pointer',
      }}
    >
      <InfoIcon
        onMouseOver={(e) => {
          setInfoAnchorEl(e.currentTarget);
        }}
        onMouseOut={() => {
          setInfoAnchorEl(null);
        }}
        sx={{
          width: '1.2rem',
          cursor: 'pointer',
        }}
      />
      <Popover
        id='mouse-over-popover'
        sx={{ pointerEvents: 'none' }}
        open={Boolean(infoAnchorEl)}
        anchorEl={infoAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        onClose={() => {
          setInfoAnchorEl(null);
        }}
        disableRestoreFocus
      >
        <Box
          sx={{
            maxWidth: '300px',
          }}
          p={1}
        >
          <Typography sx={{ p: 1 }}>
            <Trans>{p.text}</Trans>
          </Typography>
        </Box>
      </Popover>
    </Stack>
  );
}

export default function UserSpacesExplorer(p: {
  listQuery: string;
  communityId?: string;
  handle?: string;
  categories?: RequestHandle<SpaceCategory[]>;
}) {
  const { _ } = useLingui();
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  const numTiles = useMemo(
    () => (containerWidth == null ? 0 : computeNumTiles(containerWidth)),
    [containerWidth],
  );

  const containerRef = useRef<HTMLDivElement | null>(null);

  const scrollBufferRef = useRef<HTMLDivElement | null>(null);

  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const [sectionIdPendingScroll, setSectionIdPendingScroll] =
    useState<string>();

  const communityId = p.communityId === '$all' ? undefined : p.communityId;

  const history = useHistory();

  const { community, syncState, loadCommunityDetails } = useCommunityDetails({
    communityId,
    autoLoad: false,
  });

  const tagsEnabled = useMemo(() => {
    if (!community) return true;
    // Show tags to all now since co-creators can host spaces
    /*
    const ownerOrHosts = community.members?.filter((m) => {
      return !!m.roles.find((it) => it === 'OWNER' || it === 'HOST');
    });
    if (!ownerOrHosts || ownerOrHosts.length === 1) return false;
    */
    return true;
  }, [community]);

  const {
    visibleThemes,
    listQuery: themeListQuery,
    setListQuery: setThemeListQuery,
  } = useThemeSelection({
    autoLoad: true,
    communityId: communityId === '$all' ? undefined : communityId,
  });
  const [spaceFilters, setSpaceFilters] = useRecoilState(spaceFiltersAtom);

  useEffect(() => {
    if (syncState === 'loaded' && !community) {
      history.push(ROUTES.COMMUNITY_SETUP_INIT);
    }
  }, [syncState]);

  const isHost = useMemo(() => {
    return CMR.isHost(community?.roles ?? []);
  }, [community]);

  const hasSingleHost = useMemo(() => {
    return (
      community?.members?.filter((m) => CMR.isHost(m.roles ?? []))?.length === 1
    );
  }, [community]);

  useEffect(initContainerWidthTracking, []);

  const { userId, query } = useQueryParams();

  useEvt((ctx) => {
    visitSpaceSectionEvt.attach(ctx, async ({ sectionId }) => {
      setSectionIdPendingScroll(sectionId);
    });
  }, []);

  useEffect(() => {
    if (sectionIdPendingScroll) {
      setTimeout(() => {
        scrollToSection(sectionIdPendingScroll);
      }, 100);
      setSectionIdPendingScroll(undefined);
    }
  }, [sectionIdPendingScroll]);

  useEffect(loadNextPages, []);

  const { innerWidth } = useWindowSize();
  const isNarrow = !!innerWidth && innerWidth < NARROW_LAYOUT_THRESHOLD;

  const { userProfile } = useUserProfile();

  const tutorial = useTutorial();
  // Render:

  useEffect(() => {
    if (tutorial.name === GETTING_STARTED_TUTORIAL && tutorial.stepIndex == 1) {
      visitSpaceSectionEvt.post({ sectionId: 'my-coco-spaces' });
    }
  }, [tutorial]);

  useEvt(
    (ctx) => {
      notificationsEvt.attach(ctx, ({ eventName, content }) => {
        if (
          eventName &&
          (eventName === NOTIFICATION_EVENTS.INVITED_TO_SPACE_BY_MEMBER ||
            eventName === NOTIFICATION_EVENTS.NEW_OPEN_TO_JOIN_SPACE) &&
          content
        ) {
          const payload = JSON.parse(content);

          if (payload.communityId === p.communityId) {
            loadCommunityDetails();
          }
        }
      });
    },
    [communityId],
  );

  if (
    community &&
    (community.roles.length === 0 ||
      community.deactivatedAt ||
      !community.isActive)
  ) {
    return <Redirect to={`/app/dashboard/${communityId}`} />;
  }

  const viewingAsSysAdmin =
    community?.roles?.length === 1 && community.roles[0] === 'SYSADMIN';

  let sections: ReactChild[] = [];

  if ((community?.totalSpaceCount ?? -1) === 0) {
    sections = [...getWorldCorridorSections(), ...getSpaceSections()].map(
      (section, idx) => {
        const Icon = sectionIcons[section.id];
        const header = (
          <FlexRow
            className={C.sectionPrimaryHeader}
            style={{
              width: computeTileContainerWidth(numTiles) + 'px',
              margin: '30px auto 25px auto',
            }}
          >
            {Icon ? (
              <Icon style={{ marginRight: 8, width: 16, height: 16 }} />
            ) : (
              <></>
            )}
            {section.label}
            <>
              <DotSeparator
                style={{ fontSize: '0.3rem', transform: 'scale(1)' }}
              />
              <span
                className={C.helpContent}
                style={{
                  fontSize: 'var(--font-size-primary)',
                  fontWeight: 'var(--font-weight-regular)',
                }}
              >
                <Trans>{section.emptyDescription}</Trans>
              </span>
            </>
          </FlexRow>
        );

        if (section.id === 'coco-jams') {
          return (
            <>
              <UserSpaceSection
                key={section.id}
                userId={userId}
                section={section}
                query={query}
                numTiles={numTiles}
                communityId={communityId}
                isHost={isHost}
                showCommunityIcons={
                  p.communityId === '$all' &&
                  section.id !== 'coco-jams' &&
                  section.id !== 'spaces-to-remix'
                }
                containerRef={(el: HTMLDivElement | null) => {
                  sectionRefs.current[section.id] = el;
                }}
                showHostLabel={!hasSingleHost}
                tagsEnabled={tagsEnabled}
                horizontal
                communityType={community?.corridorType}
                userType={userProfile?.userType}
              />
            </>
          );
        }

        if (section.id === 'new-spaces-to-join') {
          return <></>;
        }
        return (
          <Box>
            {header}
            {section.id === 'my-coco-spaces' && (
              <FirstSpaceCTA numTiles={numTiles} />
            )}
            {section.id === 'explore-other-spaces' && <Box mt={'12rem'} />}
          </Box>
        );
      },
    );
  }

  if (!communityId || (community && (community.totalSpaceCount ?? -1) > 0)) {
    sections = [
      ...(p.handle
        ? [...getPublicSpaceSections(p.handle)]
        : [...getWorldCorridorSections(), ...getSpaceSections()]),
    ].map((section) => {
      return (
        <UserSpaceSection
          key={section.id}
          userId={userId}
          section={section}
          query={query}
          numTiles={numTiles}
          communityId={communityId}
          handle={p.handle}
          isHost={isHost}
          showCommunityIcons={
            p.communityId === '$all' &&
            section.id !== 'coco-jams' &&
            section.id !== 'spaces-to-remix'
          }
          containerRef={(el: HTMLDivElement | null) => {
            sectionRefs.current[section.id] = el;
          }}
          showHostLabel={!hasSingleHost}
          tagsEnabled={tagsEnabled}
          horizontal={!!communityId && section.id === 'coco-jams'}
          communityType={community?.corridorType}
          userType={userProfile?.userType}
        />
      );
    });
  }

  return (
    <div
      ref={containerRef}
      className={cc([C.container, { [C.narrow]: isNarrow }])}
    >
      {viewingAsSysAdmin && (
        <Alert severity='error' variant='filled'>
          <Trans>You can view this community because you are SYSADMIN</Trans>
        </Alert>
      )}
      <Box mt={1.5} />
      <FlexRow
        style={{
          paddingTop: 10,
          paddingBottom: 10,
          gap: 16,
          width: computeTileContainerWidth(numTiles) + 'px',
          margin: 'auto',
          alignItems: 'center',
          position: 'sticky',
          top: '0px',
          backgroundColor: 'white',
          zIndex: 10,
        }}
      >
        <Box
          style={{
            width: '50%',
            position: 'relative',
            flex: 1,
          }}
        >
          <HorizontalCarousal
            className={C.themeTags}
            // eslint-disable-next-line lingui/no-unlocalized-strings
            list={[{ id: 'all', name: 'All' }, ...visibleThemes].map(
              (theme) => ({
                id: theme.id,
                child: (
                  <div
                    key={theme.id}
                    className={cc([
                      C.themeTag,
                      (spaceFilters.selectedThemeIds.length === 0 &&
                        theme.id === 'all') ||
                        spaceFilters.selectedThemeIds.find(
                          (id) => id === theme.id,
                        )
                        ? C.activeItem
                        : '',
                    ])}
                    onClick={() => {
                      setSpaceFilters((sf) => {
                        let selectedThemeIds = [...sf.selectedThemeIds];
                        const themeIdx = selectedThemeIds.findIndex(
                          (id) => id === theme.id,
                        );
                        if (themeIdx !== -1)
                          selectedThemeIds.splice(themeIdx, 1);
                        else {
                          if (theme.id === 'all') selectedThemeIds = [];
                          else selectedThemeIds = [theme.id];
                        }
                        return {
                          ...sf,
                          selectedThemeIds,
                        };
                      });
                    }}
                  >
                    {getThemeLocalizedNames()[theme.name.toLowerCase()]}
                  </div>
                ),
              }),
            )}
          />
        </Box>
        <Divider
          style={{
            borderStyle: 'dashed',
          }}
          orientation='vertical'
          variant='fullWidth'
          flexItem
        />
        <ParticipantsIcon style={{ width: 24, height: 24 }} />
        <Select
          autoWidth
          size='small'
          sx={{
            fontSize: '0.8rem',
          }}
          value={spaceFilters.participantCount}
          onChange={(e) => {
            setSpaceFilters((sf) => ({
              ...sf,
              participantCount: e.target.value as string,
            }));
          }}
        >
          <MenuItem value={'any'}>Any</MenuItem>
          <MenuItem value={'1'}>1</MenuItem>
          <MenuItem value={'2'}>2</MenuItem>
          <MenuItem value={'3'}>3</MenuItem>
          <MenuItem value={'4'}>4</MenuItem>
          <MenuItem value={'5'}>5</MenuItem>
          <MenuItem value={'6'}>6</MenuItem>
          <MenuItem value={'6+'}>6+</MenuItem>
        </Select>
      </FlexRow>
      <Stack gap='0.4rem'>{sections}</Stack>
      <div ref={scrollBufferRef} style={{ marginBottom: '30px' }} />
    </div>
  );

  function loadNextPages() {
    import('components/community-member-adder/CommunityMemberAdder');
    import('components/community-members-explorer/CommunityMembersExplorer');

    if (ENABLE_SPACE_EDITOR) {
      import('components/space-editor-page/SpaceEditorPage');
    }

    import('components/space-details-modal/SpaceDetailsModal');
    import('components/jam-details-modal/JamDetailsModal');
  }

  function initContainerWidthTracking() {
    trackContainerWidth();
    window.addEventListener('resize', trackContainerWidth);
    return () => {
      window.removeEventListener('resize', trackContainerWidth);
    };
  }

  function trackContainerWidth() {
    const containerEl = containerRef.current;
    if (!containerEl) return;
    const { width } = containerEl.getBoundingClientRect();
    setContainerWidth(width);
  }

  function scrollToSection(sectionId: string) {
    const containerEl = containerRef.current;
    const sectionEl = sectionRefs.current[sectionId];
    const scrollBufEl = scrollBufferRef.current;
    if (!containerEl || !sectionEl || !scrollBufEl) return;
    const containerDims = containerEl.getBoundingClientRect();
    const sectionDims = sectionEl.getBoundingClientRect();
    const scrollDiff = sectionDims.top - containerDims.top - 70;
    if (
      containerEl.scrollHeight - containerEl.scrollTop - containerDims.height <
      scrollDiff
    ) {
      const scrollBufDims = scrollBufEl.getBoundingClientRect();
      let bufHeight =
        scrollBufDims.height +
        scrollDiff -
        (containerEl.scrollHeight -
          containerEl.scrollTop -
          containerDims.height);
      if (scrollBufDims.top < containerDims.top + containerDims.height) {
        bufHeight +=
          containerDims.top + containerDims.height - scrollBufDims.top;
      }
      scrollBufEl.style.height = bufHeight + 'px';
    }
    containerEl.scrollBy({
      top: scrollDiff,
      behavior: 'smooth',
    });
  }
}

function SpaceSectionSortControl(p: {
  sortCriteria: SortCriteria[];
  sortInput: SortInput;
  setSortInput: (input: SortInput) => void;
}) {
  return (
    <PopupState variant='popover' popupId='space-gallery-options-menu'>
      {(popupState) => {
        return (
          <>
            <IconButton {...bindTrigger(popupState)}>
              <SortIcon
                style={{
                  height: '1rem',
                  width: '1rem',
                  color: 'var(--color-primary)',
                }}
              />
            </IconButton>
            <Menu {...bindMenu(popupState)}>
              <MenuItem disabled>
                <Trans>Sort By</Trans>
              </MenuItem>
              {p.sortCriteria.map((sc) => (
                <MenuItem
                  onClick={() => {
                    popupState.close();
                    p.setSortInput({ column: sc.column, dir: sc.dir });
                  }}
                  key={`${sc.column}:${sc.dir}`}
                >
                  <ListItemIcon>
                    {p.sortInput.column === sc.column &&
                      p.sortInput.dir === sc.dir && <CheckCircleOutlineIcon />}
                  </ListItemIcon>
                  <ListItemText>{sc.label}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </>
        );
      }}
    </PopupState>
  );
}

const tileWidth = 235 + /* Border: */ 2;
const tileGap = 30;

// (moved earlier; duplicate removed)

export function SpaceGrid(p: {
  section: SpaceSection;
  count?: number;
  showArchived?: boolean;
  isLoading?: boolean;
  showCommunityIcons?: boolean;
  showDateTag?: boolean;
  spaces?: Space[];
  jams?: Jam[];
  numTiles: number;
  sortInput: SortInput;
  setSortInput: (c: SortInput) => void;
  onScrollEnd?: () => any | Promise<any>;
  showHostLabel?: boolean;
  tagsEnabled?: boolean;
  containerRef?: (el: HTMLDivElement | null) => void;
  horizontal?: boolean;
  communityType?: string;
  userType?: string;
}) {
  const { _ } = useLingui();
  const [spaceFilters, setSpaceFilters] = useRecoilState(spaceFiltersAtom);
  const width = computeTileContainerWidth(p.numTiles);
  const Icon = sectionIcons[p.section.id];
  const worldSection = getWorldCorridorSections().find(
    (section) => section.id === p.section.id,
  );
  const [isCreatingSpace, setCreatingSpace] = useState(false);
  const history = useHistory();
  const sectionLabel =
    p.section.id === 'coco-jams' && p.horizontal
      ? p.section.label
      : p.section.altLabel || p.section.label;

  return (
    <div
      style={{
        width: width + 'px',
        margin: 'auto',
      }}
      ref={p.containerRef}
    >
      {p.section.hideHeading && <Box mt={3} />}
      {!p.section.hideHeading && (
        <>
          <FlexRow
            className={C.sectionPrimaryHeader}
            style={{
              marginTop: '30px',
              marginBottom: '25px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              width: '100%',
              boxSizing: 'border-box',
            }}
            alignItems='center'
          >
            {Icon ? (
              <Icon style={{ marginRight: 8, width: 16, height: 16 }} />
            ) : (
              <></>
            )}
            <LightTooltip title={p.section.description} arrow>
              <span>{sectionLabel}</span>
            </LightTooltip>
            <>
              {p.count != null || p.section.horizontalDescription ? (
                <DotSeparator
                  style={{ fontSize: '0.3rem', transform: 'scale(1)' }}
                />
              ) : undefined}
            </>
            {p.section.horizontalDescription ? (
              <span
                className={C.helpContent}
                style={{
                  fontSize: 'var(--font-size-primary)',
                  fontWeight: 'var(--font-weight-regular)',
                }}
              >
                {p.section.horizontalDescription}
              </span>
            ) : (
              <span
                className={C.helpContent}
                style={{
                  fontSize: 'var(--font-size-primary)',
                  fontWeight: 'var(--font-weight-regular)',
                }}
              >
                {p.count}
              </span>
            )}
            <>
              {width > 550 && p.section.presets.length > 1 && p.tagsEnabled && (
                <TagList
                  small
                  tags={p.section.presets}
                  onSelect={handleSelectTag}
                  selectedId={spaceFilters.sectionPresets[p.section.id]}
                  style={{
                    marginLeft: '2rem',
                  }}
                />
              )}
            </>
            {p.isLoading && (
              <LoaderMessage
                mode='subtle'
                style={{
                  fontSize: '1rem',
                  fontWeight: '100',
                  margin: '0 10px',
                }}
              />
            )}
            <FlexSep grow />
            {!worldSection && (
              <SpaceSectionSortControl
                sortCriteria={p.section.sortCriteria}
                sortInput={p.sortInput}
                setSortInput={p.setSortInput}
              />
            )}
            {p.section.id === 'coco-jams' && p.horizontal && (
              <Link
                variant='text'
                onClick={() => {
                  history.push('/app/dashboard/$all');
                }}
              >
                <Typography color='var(--color-primary)' variant='body2'>
                  <Trans>See all</Trans>
                </Typography>
              </Link>
            )}
            {p.section.infoText && (
              <Box ml={1}>
                <SectionInfo text={p.section.infoText} />
              </Box>
            )}
          </FlexRow>
          <>
            {width <= 550 && p.section.presets.length > 1 && p.tagsEnabled && (
              <TagList
                small
                tags={p.section.presets}
                onSelect={handleSelectTag}
                selectedId={spaceFilters.sectionPresets[p.section.id]}
                style={{
                  marginBottom: '1rem',
                }}
              />
            )}
          </>
        </>
      )}
      {/* Empty section message just after heading row, inside the grid container */}
      {!p.isLoading && !p.horizontal && (
        (() => {
          const isJam = p.section.id === 'coco-jams' || p.section.id === 'featured-community-spaces';
          const empty = isJam ? (p.jams?.length ?? 0) === 0 : (p.spaces?.length ?? 0) === 0;
          return empty ? (
            <EmptySectionMessage numTiles={p.numTiles} section={p.section} />
          ) : null;
        })()
      )}
      {p.horizontal ? (
        <Box
          sx={{
            position: 'relative',
          }}
        >
          <ScrollableContainer
            gap='1.3rem'
            sx={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
            elements={
              p.jams
                ?.filter((jam) => {
                  if (jam.corridorType) {
                    return jam.corridorType === p.communityType;
                  }
                  return true;
                })
                .map((space) => (
                  <JamCard
                    {...{ arrow: false }}
                    key={space.id}
                    jamId={space.id}
                    containerWidth={tileWidth}
                    previewHeight={tileWidth * (3 / 4)}
                    style={{
                      flexGrow: 0,
                      flexShrink: 0,
                      marginRight: '1rem',
                      margin: 0,
                    }}
                    showCommunityIcon={false}
                    showHostLabel={false}
                    showRecencyTag={false}
                  />
                )) || []
            }
          />
        </Box>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: range(p.numTiles)
              .map(() => `${tileWidth}px`)
              .join(' '),
            gridGap: tileGap,
          }}
        >
          {p.section.id === 'coco-jams' ||
            p.section.id === 'featured-community-spaces'
            ? p.jams
              ?.filter((jam) => {
                if (jam.corridorType) {
                  return jam.corridorType === p.userType;
                }
                return true;
              })
              .map((space) => (
                <JamCard
                  {...{ arrow: false }}
                  key={space.id}
                  jamId={space.id}
                  containerWidth={tileWidth}
                  previewHeight={tileWidth * (3 / 4)}
                  style={{
                    flexGrow: 0,
                    flexShrink: 0,
                    marginRight: '1rem',
                    margin: 0,
                  }}
                  showCommunityIcon={false}
                  showHostLabel={false}
                  showRecencyTag={false}
                />
              ))
            : [
              // Always show the NewSpaceCard as the first tile
              p.section.id === 'my-coco-spaces' ? (
                <NewSpaceCard key={'__new-space-card'} style={{ width: tileWidth }} />
              ) : null,
              // Followed by the actual spaces
              ...(p.spaces || []).map((space) =>
                space.archivedAt && !p.showArchived ? null : (
                  <SpaceCard
                    {...{ arrow: false }}
                    key={space.id}
                    spaceId={space.id}
                    containerWidth={tileWidth}
                    previewHeight={tileWidth * (3 / 4)}
                    style={{
                      flexGrow: 0,
                      flexShrink: 0,
                      marginRight: '1rem',
                      margin: 0,
                    }}
                    showCommunityIcon={p.showCommunityIcons}
                    showHostLabel={p.showHostLabel}
                    showRecencyTag={p.section.showRecencyTag}
                    showDateTag={p.showDateTag}
                  />
                )),
            ]}
        </div>
      )}
      {isCreatingSpace && (
        <Suspense fallback={null}>
          <NewSpaceModal
            onClose={() => {
              setCreatingSpace(false);
            }}
            onSuccess={() => {
              setCreatingSpace(false);
            }}
          />
        </Suspense>
      )}
    </div>
  );

  function handleSelectTag(id: string) {
    setSpaceFilters((filters) => ({
      ...filters,
      sectionPresets: {
        ...filters.sectionPresets,
        [p.section.id]: id,
      },
    }));
  }
}

const computeTileContainerWidth = (numTiles: number) => {
  return tileWidth * numTiles + tileGap * (numTiles - 1);
};

const computeNumTiles = (containerWidth: number) => {
  let count = Math.floor(containerWidth / tileWidth);
  while (
    tileWidth * count +
      /* Gap between tiles and on either side */ tileGap * (count + 1) >
    containerWidth
  ) {
    count -= 1;
  }
  if (count < 1) return 1;
  return count;
};

export function UserSpaceSection(p: {
  userId: string;
  numTiles: number;
  communityId?: string;
  handle?: string;
  showCommunityIcons?: boolean;
  query: any;
  tagsEnabled?: boolean;
  isHost: boolean;
  section: SpaceSection;
  containerRef: (el: HTMLDivElement | null) => void;
  showHostLabel?: boolean;
  horizontal?: boolean;
  userType?: string;
  communityType?: string;
}) {
  const [filters] = useRecoilState(spaceFiltersAtom);
  const defaultSortCriteria = p.section.sortCriteria.find((it) => it.isDefault);
  if (!defaultSortCriteria) {
    throw new Error(
      // eslint-disable-next-line lingui/no-unlocalized-strings
      `Expected default sort criteria in section: ${p.section.id}`,
    );
  }
  const [sortInput, setSortInput] = useState<SortInput>({
    column: defaultSortCriteria.column,
    dir: defaultSortCriteria.dir,
  });

  const presetId = p.section.presets.find((preset) => {
    return preset.id === filters.sectionPresets[p.section.id];
  })?.id;

  if (!presetId)
    throw new Error(
      // eslint-disable-next-line lingui/no-unlocalized-strings
      `Invalid preset found: ${p.section.id} -> ${filters[p.section.id]}`,
    );

  const width = computeTileContainerWidth(p.numTiles);

  const worldSection = getWorldCorridorSections().find(
    (section) => section.id === p.section.id,
  );

  const sectionId = p.section.id;

  const spaceQueryHandle = useSpacesQuery(
    {
      sectionId: sectionId,
      communityId: p.communityId,
      handle: p.handle,
      filter: [sectionId, presetId],
      userId: p.userId,
      query: p.query,
      sortCriteria: sortInput.column,
      sortDir: sortInput.dir,
      themeIds: filters.selectedThemeIds,
      overrideParams: {
        participantCount: filters.participantCount,
      },
    },
    {
      limit:
        p.section.id === 'new-spaces-to-join' ? p.numTiles : p.numTiles * 3,
    },
  );

  const [activeSectionId, setActiveSectionId] = useRecoilState(
    SpaceExplorerStateAtom,
  );

  useEffect(() => {
    if (p.numTiles > 0) {
      spaceQueryHandle.fetchInitialSpaces({
        skipIfLoaded: false,
      });
    }
  }, [
    p.communityId,
    sortInput,
    p.userId,
    p.query,
    filters.sectionPresets[p.section.id],
    filters.selectedThemeIds,
    p.numTiles,
    filters.participantCount,
  ]);

  useEvt(
    (ctx) => {
      if (p.section.id === 'upcoming' || p.section.id === 'my-coco-spaces') {
        spaceCreatedEvt.attach(ctx, () => {
          spaceQueryHandle.fetchInitialSpaces({
            skipIfLoaded: false,
          });
        });
      } else if (p.section.id === 'past') {
        spaceEndedEvt.attach(ctx, () => {
          spaceQueryHandle.fetchInitialSpaces({
            skipIfLoaded: false,
          });
        });
      }
    },
    [p.section.id, p.numTiles],
  );

  useEvt(
    (ctx) => {
      if (p.section.id !== 'new-spaces-to-join') return;
      notificationsEvt.attach(ctx, ({ eventName, content }) => {
        if (
          eventName &&
          (eventName === NOTIFICATION_EVENTS.INVITED_TO_SPACE_BY_MEMBER ||
            eventName === NOTIFICATION_EVENTS.NEW_OPEN_TO_JOIN_SPACE) &&
          content
        ) {
          const payload = JSON.parse(content);

          if (payload.communityId === p.communityId) {
            spaceQueryHandle.fetchInitialSpaces({
              skipIfLoaded: false,
            });
          }
        }
      });
    },
    [p.section.id, p.numTiles],
  );

  if (
    p.section.id === 'new-spaces-to-join' &&
    spaceQueryHandle?.data?.count === 0
  ) {
    return <></>;
  }

  const isJam =
    p.section.id === 'coco-jams' ||
    p.section.id === 'featured-community-spaces';

  return (
    <div
      style={{
        visibility: width <= 0 ? 'hidden' : 'visible',
      }}
    >
      <SpaceGrid
        section={p.section}
        count={spaceQueryHandle?.data?.count}
        spaces={!isJam ? spaceQueryHandle?.data?.spaces : []}
        jams={isJam ? spaceQueryHandle?.data?.spaces : []}
        numTiles={p.numTiles}
        containerRef={p.containerRef}
        showArchived={!!p.query}
        showCommunityIcons={p.showCommunityIcons}
        showHostLabel={p.showHostLabel}
        showDateTag={!worldSection}
        isLoading={spaceQueryHandle.syncState === 'loading'}
        tagsEnabled={p.tagsEnabled}
        horizontal={p.horizontal}
        communityType={p.communityType}
        userType={p.userType}
        {...{ sortInput, setSortInput }}
      />
      {spaceQueryHandle.hasMore && !p.horizontal && (
        <div style={{ width, margin: 'auto', marginTop: '5px' }}>
          <LoadingButton
            variant='text'
            onClick={() => {
              spaceQueryHandle.fetchNextSpaces();
            }}
            disabled={spaceQueryHandle.syncState === 'loading'}
            loading={spaceQueryHandle.syncState === 'loading'}
          >
            <Trans>Load more</Trans>
          </LoadingButton>
        </div>
      )}
    </div>
  );
}

const FirstSpaceCTA = (p: { numTiles: number }) => {
  return (
    <>
      <div
        style={{
          color: 'var(--color-subtext)',
          textAlign: 'left',
          margin: 'auto',
          width: computeTileContainerWidth(p.numTiles),
        }}
      >
        <NewSpaceCard style={{ marginTop: '30px' }} />
      </div>
    </>
  );
};

const EmptySectionMessage = (p: {
  section: SpaceSection;
  numTiles: number;
}) => {
  return (
    <div
      style={{
        color: 'var(--color-subtext)',
        textAlign: 'left',
        margin: 'auto',
        width: computeTileContainerWidth(p.numTiles),
        marginBottom: '1.5rem',
      }}
    >
      <Trans>
        {`${p.section.description}${p?.section?.id === 'spaces-to-remix' || p?.section?.id === 'coco-jams'
            ? ''
            : ' ' + t`will appear here.`
          }`}
      </Trans>
    </div>
  );
};
